import {
  authenticationUrl,
  isAuthenticatedUrl,
  logoutUrl,
  getSelfServiceAccountOverviewUrl,
  getIdentityDetails,
  getLoyaltyType,
  getResidentialAddress,
  getPostalAddress,
  getMembershipCardNumber,
  getPortfolioAssitanceProductsForDirectDebit,
  getMemberNumber,
  getSelfServiceMakePaymentUrl,
  updateContactInformation,
  searchIdentitiesUrl,
  searchOktaAccountUrl,
  updateAddressInformation,
  isUserNameTaken,
  matchIdentityUrl,
  isProvisionedUrl,
  setPasswordUrl,
  matchAndSetIdentityUrl,
  changePasswordUrl,
  recoverAccountInteractionUrl,
} from '@/constants/forms-api.const';
import { RequestServices as _http } from '@/services/common/request.services';
import { IdentityDetails, Address, OwnedAssistanceProduct, IServiceResponse } from '@/interfaces';
import { JssSearchIdentityRequest } from '@/interfaces/self-service/search-identity.interface';

export const AccountServices = {
  /**
   * Fetch Identity Details
   * @returns
   */
  fetchIdentityDetails(forceRefresh?: boolean): Promise<IdentityDetails> {
    return _http.HttpGet(`${getIdentityDetails}?forceRefresh=${forceRefresh?.toString() ?? 'false'}`);
  },

  /**
   * Get account overview URL
   * @returns
   */
  getSelfServiceAccountOverviewPage(): Promise<string> {
    return _http.HttpGet(`${getSelfServiceAccountOverviewUrl}`);
  },

  /**
   * Get make a payment URL
   * @returns
   */
  getSelfServiceMakePaymentUrl(): Promise<string> {
    return _http.HttpGet(`${getSelfServiceMakePaymentUrl}`);
  },

  async getLoyaltyType(externalSystemId: string | null): Promise<string | null> {
    const url = `${getLoyaltyType}?externalSystemId=${externalSystemId}`;
    return await _http.HttpGet(url);
  },

  async getResidentialAddress(externalSystemId: string | null): Promise<Address> {
    const url = `${getResidentialAddress}?externalSystemId=${externalSystemId}`;
    return await _http.HttpGet(url);
  },

  async getPostalAddress(externalSystemId: string | null): Promise<Address> {
    const url = `${getPostalAddress}?externalSystemId=${externalSystemId}`;
    return await _http.HttpGet(url);
  },

  async getMembershipCardNumber(externalSystemId: string | null): Promise<string | null> {
    const url = `${getMembershipCardNumber}?externalSystemId=${externalSystemId}`;
    return await _http.HttpGet(url);
  },

  async getPortfolioAssitanceProductsForDirectDebit(): Promise<OwnedAssistanceProduct[]> {
    const url = `${getPortfolioAssitanceProductsForDirectDebit}`;
    return await _http.HttpGet(url);
  },

  async getMembershipNumber(): Promise<IServiceResponse<string>> {
    const url = `${getMemberNumber}`;
    return await _http.HttpGet(url);
  },

  authenticate(token: string, clientId?: string): Promise<{ success: boolean; error: string }> {
    return _http.HttpPost(`${authenticationUrl}`, {
      token: token,
      redirect_uri: `${window.location.protocol}//${window.location.host}${process.env.VUE_APP_ENTRA_CALLBACK}`,
      client_id: clientId || '',
    });
  },

  /**
   * Check login status in sitecore 10
   * @returns
   */
  isAuthenticated(): Promise<boolean> {
    return _http.HttpGet(`${isAuthenticatedUrl}`);
  },

  /**
   * Logout sitecore 10
   * @returns
   */
  logOut(): Promise<string> {
    return _http.HttpGet(logoutUrl);
  },

  updateContactInformation(
    d365Id: string,
    emailAddress: string,
    primaryPhoneNumber: string,
    additionalPhoneNumber: string
  ): Promise<{ success: boolean; error: string }> {
    return _http.HttpPut(`${updateContactInformation}?d365Id=${d365Id}`, {
      emailAddress,
      primaryPhoneNumber,
      additionalPhoneNumber,
    });
  },

  /**
   * Search identities for username recovery
   * @param firstName
   * @param lastName
   * @param postcode
   * @param dateOfBirth yyyy-MM-dd
   * @returns
   */
  searchIdentities(request: JssSearchIdentityRequest) {
    return _http.HttpPost(searchIdentitiesUrl, request);
  },

  /**
   * Search okta profile
   * @param email Email address to use in search
   * @returns Profile if found
   */
  searchOktaAccountByEmail(
    email: string
  ): Promise<{ hasOktaId: boolean; isNewEmailAddress: boolean; isInactive: boolean }> {
    return _http.HttpPost(searchOktaAccountUrl, {
      email,
    });
  },

  matchIdentity(
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    postcode: string,
    emailAddress: string
  ): Promise<
    {
      hasOktaId: boolean;
      isInactive: boolean;
      differentMatchedEmail: boolean;
    }[]
  > {
    return _http.HttpPost(matchIdentityUrl, {
      firstName,
      lastName,
      dateOfBirth,
      postcode,
      emailAddress,
    });
  },

  matchAndSetIdentity(
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    postcode: string,
    emailAddress: string,
    membershipCardNumber: string
  ): Promise<{ created: boolean }> {
    return _http.HttpPost(matchAndSetIdentityUrl, {
      firstName,
      lastName,
      dateOfBirth,
      postcode,
      emailAddress,
      membershipCardNumber,
    });
  },

  isProvisioned(email: string) {
    return _http.HttpPost(isProvisionedUrl, {
      email,
    });
  },

  setPassword(password: string, isSelfServicePasswordChange: boolean = false) {
    return _http.HttpPost(setPasswordUrl, {
      password,
      isSelfServicePasswordChange,
    });
  },

  updateAddressInformation(
    d365Id: string,
    residentialAddress?: Partial<Address>,
    postalAddress?: Partial<Address>
  ): Promise<{ success: boolean; error: string }> {
    return _http.HttpPut(`${updateAddressInformation}?d365Id=${d365Id}`, {
      residentialAddress,
      postalAddress,
    });
  },

  isUserNameTaken(email: string): Promise<boolean> {
    return _http.HttpGet(isUserNameTaken.replace('{0}', encodeURIComponent(email)));
  },

  changePassword(currentPassword: string, newPassword?: string): Promise<{ success: boolean; error: string }> {
    return _http.HttpPost(`${changePasswordUrl}`, {
      currentPassword,
      newPassword,
    });
  },

  recoverAccountInteraction(d365Id: string, oktaId: string) {
    return _http.HttpPost(recoverAccountInteractionUrl.replace('{0}', d365Id).replace('{1}', oktaId || ''));
  },
} as const;
